import { useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { adminApi, type AdminSession, type GeoStats } from '../../api/admin';
import { SessionsMap } from './SessionsMap';

const POLL_MS = 15_000;

type StatusFilter = 'active' | 'expired' | 'logged_out' | 'all';
type ViewMode = 'table' | 'map';

export function SessionsTab() {
  const [status, setStatus] = useState<StatusFilter>('active');
  const [emailFilter, setEmailFilter] = useState('');
  const [appFilter, setAppFilter] = useState<string>('');
  const [view, setView] = useState<ViewMode>('table');
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const key = ['admin/sessions', status, emailFilter, appFilter] as const;
  const { data: sessions = [], isLoading } = useSWR<AdminSession[]>(
    key,
    () =>
      adminApi.listSessions({
        ...(emailFilter ? { email: emailFilter } : {}),
        ...(appFilter ? { app: appFilter } : {}),
        status,
        limit: 200,
      }),
    { refreshInterval: POLL_MS },
  );

  // Browser-geo consent rate (last 30d, Pulse-local). Refreshed less often
  // than the session list since the underlying stat moves slowly.
  const { data: geoStats } = useSWR<GeoStats>(
    'admin/sessions/geo-stats',
    () => adminApi.geoStats(),
    { refreshInterval: 60_000 },
  );

  const totals = useMemo(() => {
    let active = 0;
    let loggedOut = 0;
    let expired = 0;
    for (const s of sessions) {
      if (s.is_active) active++;
      else if (s.logout_at) loggedOut++;
      else expired++;
    }
    return { active, loggedOut, expired };
  }, [sessions]);

  const apps = useMemo(() => {
    const set = new Set<string>();
    for (const s of sessions) set.add(s.app);
    return Array.from(set).sort();
  }, [sessions]);

  async function handleRevoke(s: AdminSession) {
    setActionErr(null);
    setActionMsg(null);
    if (s.app !== 'pulse') {
      setActionErr(`Cannot revoke ${s.app} sessions from Pulse — log out via the ${s.app} admin.`);
      return;
    }
    if (
      !window.confirm(
        `Log out ${s.email} (${s.app}) — session ${s.id.slice(0, 8)}…?\nThey'll need to OTP again.`,
      )
    )
      return;
    try {
      await adminApi.revokeSession(s.id);
      setActionMsg(`Revoked ${s.email}'s ${s.app} session.`);
      void mutate(key);
    } catch (err) {
      setActionErr(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleDedupe() {
    setActionErr(null);
    setActionMsg(null);
    if (
      !window.confirm(
        'Sweep duplicate active sessions across all users (Pulse only)?\nKeeps the most recent per email, marks the rest logged-out. Old rows stay in the audit trail.',
      )
    )
      return;
    try {
      const r = await adminApi.dedupeActiveSessions();
      setActionMsg(`Dedupe complete — ${r.revoked} stale session${r.revoked === 1 ? '' : 's'} revoked.`);
      void mutate(key);
    } catch (err) {
      setActionErr(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-cockpit-dim">
            Sessions ({sessions.length})
          </h3>
          <p className="text-xs text-cockpit-dim">
            Where users log in across the QEA stack. Active {totals.active} · logged out{' '}
            {totals.loggedOut} · expired {totals.expired}.
          </p>
          {geoStats && geoStats.asked > 0 && (
            <p className="mt-1 text-xs text-cockpit-dim">
              Browser-geo consent (last {geoStats.window_days}d, Pulse): {geoStats.consent_rate_pct}%
              granted ({geoStats.granted}/{geoStats.asked}), {geoStats.denied} denied,{' '}
              {geoStats.unsupported} unsupported.
              {geoStats.with_browser_geo > 0 && geoStats.avg_accuracy_m != null && (
                <>
                  {' '}
                  {geoStats.with_browser_geo} session
                  {geoStats.with_browser_geo === 1 ? '' : 's'} with precise location —
                  avg <strong>±{geoStats.avg_accuracy_m}m</strong>, best ±{geoStats.best_accuracy_m}m.
                </>
              )}
            </p>
          )}
        </div>

        <div className="ml-auto flex items-end gap-2 text-xs">
          <div className="inline-flex overflow-hidden rounded border border-cockpit-border">
            <button
              type="button"
              onClick={() => setView('table')}
              className={
                view === 'table'
                  ? 'bg-cockpit-text px-3 py-1 text-cockpit-bg'
                  : 'px-3 py-1 text-cockpit-dim hover:bg-cockpit-border/30'
              }
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setView('map')}
              className={
                view === 'map'
                  ? 'bg-cockpit-text px-3 py-1 text-cockpit-bg'
                  : 'px-3 py-1 text-cockpit-dim hover:bg-cockpit-border/30'
              }
            >
              Map
            </button>
          </div>
          <button
            type="button"
            onClick={handleDedupe}
            className="rounded border border-cockpit-warn px-2 py-1 text-cockpit-warn hover:bg-cockpit-warn/10"
            title="Sweep duplicate active Pulse sessions per user"
          >
            Dedupe duplicates
          </button>
          <label className="flex flex-col">
            <span className="mb-1 text-cockpit-dim">App</span>
            <select
              value={appFilter}
              onChange={(e) => setAppFilter(e.target.value)}
              className="rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            >
              <option value="">All apps</option>
              {apps.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col">
            <span className="mb-1 text-cockpit-dim">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
              className="rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            >
              <option value="active">Active</option>
              <option value="logged_out">Logged out</option>
              <option value="expired">Expired</option>
              <option value="all">All</option>
            </select>
          </label>
          <label className="flex flex-col">
            <span className="mb-1 text-cockpit-dim">Email contains</span>
            <input
              type="text"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="filter…"
              className="rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
            />
          </label>
        </div>
      </div>

      {actionMsg && (
        <div className="rounded border border-cockpit-ok/40 bg-cockpit-ok/10 px-3 py-2 text-xs text-cockpit-ok">
          {actionMsg}
        </div>
      )}
      {actionErr && (
        <div className="rounded border border-cockpit-crit/40 bg-cockpit-crit/10 px-3 py-2 text-xs text-cockpit-crit">
          {actionErr}
        </div>
      )}

      {view === 'map' && <SessionsMap sessions={sessions} />}

      {view === 'table' && (
      <div className="overflow-x-auto rounded border border-cockpit-border">
        <table className="w-full min-w-[1200px] text-sm">
          <thead className="bg-cockpit-panel text-xs uppercase tracking-wider text-cockpit-dim">
            <tr>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">App</th>
              <th className="px-3 py-2 text-left">IP</th>
              <th className="px-3 py-2 text-left">Geo</th>
              <th className="px-3 py-2 text-left">Device</th>
              <th className="px-3 py-2 text-left">Connection</th>
              <th className="px-3 py-2 text-left">Fingerprint</th>
              <th className="px-3 py-2 text-left">Login</th>
              <th className="px-3 py-2 text-left">Last seen</th>
              <th className="px-3 py-2 text-left">Duration</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <SessionRow key={`${s.app}:${s.id}`} s={s} onRevoke={() => void handleRevoke(s)} />
            ))}
            {sessions.length === 0 && !isLoading && (
              <tr>
                <td colSpan={12} className="px-3 py-6 text-center text-sm text-cockpit-dim">
                  no sessions match the filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function SessionRow({ s, onRevoke }: { s: AdminSession; onRevoke: () => void }) {
  const geo = [s.geo_city, s.geo_region, s.geo_country].filter(Boolean).join(', ') || '—';
  const device = `${s.browser} · ${s.os}`;
  const statusTone = s.is_active
    ? 'text-cockpit-ok'
    : s.logout_at
      ? 'text-cockpit-dim'
      : 'text-cockpit-warn';
  const statusLabel = s.is_active ? 'active' : s.logout_at ? 'logged out' : 'expired';
  const canRevoke = s.is_active && s.app === 'pulse';

  return (
    <tr className="border-t border-cockpit-border align-top">
      <td className="px-3 py-2 font-mono text-xs">{s.email}</td>
      <td className="px-3 py-2 text-xs uppercase tracking-wider text-cockpit-dim">{s.app}</td>
      <td className="px-3 py-2 font-mono text-xs">{s.ip ?? '—'}</td>
      <td className="px-3 py-2 text-xs">{geo}</td>
      <td className="px-3 py-2 text-xs">{device}</td>
      <td className="px-3 py-2 text-xs">
        <ConnectionTag s={s} />
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-cockpit-dim" title={s.fingerprint ?? ''}>
        {s.fingerprint ? `${s.fingerprint.slice(0, 8)}…` : '—'}
      </td>
      <td className="px-3 py-2 text-xs">{fmtTime(s.created_at)}</td>
      <td className="px-3 py-2 text-xs">{fmtTime(s.last_seen_at)}</td>
      <td className="px-3 py-2 text-xs">{fmtDuration(s.duration_sec)}</td>
      <td className={`px-3 py-2 text-xs uppercase tracking-wider ${statusTone}`}>{statusLabel}</td>
      <td className="px-3 py-2 text-right">
        {canRevoke && (
          <button
            type="button"
            onClick={onRevoke}
            className="rounded border border-cockpit-crit/40 px-2 py-0.5 text-[11px] uppercase tracking-wider text-cockpit-crit hover:bg-cockpit-crit/10"
            title="Log this session out immediately"
          >
            Revoke
          </button>
        )}
      </td>
    </tr>
  );
}

function ConnectionTag({ s }: { s: AdminSession }) {
  const type = s.connection_type ?? 'unknown';
  const label = s.connection_label || (s.geo_isp ?? '') || '—';

  const palette: Record<string, { fg: string; bg: string; border: string; icon: string }> = {
    trusted_office: {
      fg: 'text-cockpit-ok',
      bg: 'bg-cockpit-ok/10',
      border: 'border-cockpit-ok/40',
      icon: '🏢',
    },
    residential: {
      fg: 'text-cockpit-dim',
      bg: 'bg-cockpit-border/20',
      border: 'border-cockpit-border',
      icon: '🏠',
    },
    mobile: {
      fg: 'text-cockpit-dim',
      bg: 'bg-cockpit-border/20',
      border: 'border-cockpit-border',
      icon: '📱',
    },
    hosting: {
      fg: 'text-cockpit-crit',
      bg: 'bg-cockpit-crit/10',
      border: 'border-cockpit-crit/50',
      icon: '🚨',
    },
    tor: {
      fg: 'text-cockpit-crit',
      bg: 'bg-cockpit-crit/15',
      border: 'border-cockpit-crit',
      icon: '🧅',
    },
    unknown: {
      fg: 'text-cockpit-dim',
      bg: '',
      border: 'border-cockpit-border',
      icon: '·',
    },
  };
  const tone = palette[type] ?? palette.unknown!;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[11px] ${tone.fg} ${tone.bg} ${tone.border}`}
      title={label}
    >
      <span>{tone.icon}</span>
      <span className="whitespace-nowrap">{type.replace('_', ' ')}</span>
    </span>
  );
}

function fmtTime(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function fmtDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}d`;
}
